import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormAnswer, FormTemplateQuestionFieldOption } from 'graphql-common';
import { BaseItem, CellProps } from '@lib/components/Table/types';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Typography from '@lib/components/Typography/Typography';
import useDocumentsMap from '@lib/utils/fetch/hooks/useDocumentsMap';
import getDocumentAnswerStatuses from './getDocumentAnswerStatuses';
import {
  DocumentLockedMessage,
  DocumentPreviewOrUnavailable,
  DocumentReadChip,
  getDocumentIds,
} from './DocumentResponseParts';
import styles from './DocumentResponseCell.module.scss';

export interface DocumentResponseCellRow extends BaseItem {
  options?: FormTemplateQuestionFieldOption[] | null;
  answer?: Pick<FormAnswer, 'resp' | 'documentationVersionViews'> | null;
}

type Props<T extends DocumentResponseCellRow> = CellProps<T> & {
  // Cross-answer fallback: built once at the table/page level by walking ALL
  // answers' `documentationVersionViews` and indexing by `documentationId`.
  // Used to recover reading time on `READ_PREVIOUSLY` rows whose view was
  // attached to a sibling answer in the same submission (the per-resp
  // `documentationVersionViewId` is intentionally null on those rows).
  readingDurationByDocumentationId?: Map<string, number>;
  onDocumentClick?: (documentationId: string) => void;
  // EDM-locked render (see `DocumentLockedMessage`).
  isViewLocked?: boolean;
  isFullAdmin?: boolean;
  onUpgradePlan?: () => void;
};

function DocumentResponseCell<T extends DocumentResponseCellRow>(
  props: Props<T>,
) {
  const {
    item,
    readingDurationByDocumentationId,
    onDocumentClick,
    isViewLocked,
    isFullAdmin,
    onUpgradePlan,
  } = props;
  const { t } = useTranslation();
  const itemOptions = item.options;
  const options = useMemo(
    () => (itemOptions || []) as FormTemplateQuestionFieldOption[],
    [itemOptions],
  );
  const docIds = useMemo(() => getDocumentIds(options), [options]);

  const { documentsMap, loading } = useDocumentsMap(docIds);

  if (!docIds.length) {
    if (isViewLocked) {
      return (
        <DocumentLockedMessage
          t={t}
          isFullAdmin={isFullAdmin}
          onUpgradePlan={onUpgradePlan}
        />
      );
    }
    return <TextCell {...props} value="-" />;
  }

  if (loading) {
    return <CircleLoader />;
  }

  const statuses = getDocumentAnswerStatuses(
    options,
    item.answer,
    readingDurationByDocumentationId,
  );

  return (
    <div className={styles.documentList}>
      {statuses.map((status) => (
        <div key={status.option.id} className={styles.documentRow}>
          <div className={styles.documentRowPreview}>
            <DocumentPreviewOrUnavailable
              t={t}
              doc={documentsMap.get(status.docId)}
              docId={status.docId}
              onDocumentClick={onDocumentClick}
            />
          </div>
          <div>
            <DocumentReadChip t={t} status={status} />
          </div>
          <div>
            <Typography variant="caption" className={styles.documentRowTime}>
              {status.readingTime}
            </Typography>
          </div>
        </div>
      ))}
    </div>
  );
}

export default DocumentResponseCell;
